import {DOM, JSX} from "/@lib@/commons/xml/dom.js";
import {IReg, REG} from "/@lib@/commons/registry.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {BaseElementAsync, MsgLabel, OSkinableInit} from "/@back@/commons/basis.js";
import {Button} from "/@back@/commons/widgets/buttons.js";
import {POPUP} from "/@back@/commons/widgets/popups.js";
import {
	CID,
	CidHost,
	CidManifest,
	CidProcess,
	CidSession,
	CidStep,
	CidWebAuthStep,
	ECidSessionState,
	ICidManifestPointer,
	ICidStepResult,
	ICidUiHandler,
	OCidSessionConfig
} from "/@lib@/core/cid.js";
import {IUniverseEnv} from "/@lib@/core/universe.js";
import {ERROR} from "/@lib@/core/errorReport.js";
import {UiThemeDeskFeat} from "/@back@/core/plugins/optionsPlg.js";

/**
 * Intéraction CID avec sélection du manifest et du process si besoin
 */
export interface CidInteractionByStepsUi extends BaseElementAsync {
	initialize(init?: OCidInteractionByStepsUiInit): this;
}

export interface OCidInteractionByStepsUiInit extends OSkinableInit {
	/** Restriction des serveurs cid possibles fournit par le serveur via un is. */
	cidHostIs?: string[] | string[][]

	/** Ajoute un champ libre de saisie de l'URL du manifest CID. */
	cidManifestUrlInput?: boolean

	/** Filtre des hosts par rapport au "use"
	 * 'both' par défaut
	 **/
	cidHostUse?: 'input' | 'output' | 'both'

	/** Alternative : imposition d'un manifest précis (independamment de ceux fournis par le serveur).*/
	//manifestUrl?: string A voir si usage...

	/** Restriction des process possibles via une fonction. */
	cidProcessSelector?: (ctx: ICidCtx) => Promise<CidProcess[]>

	/** Si cidProcessSelector null, restriction des process possibles via un is.
	 * @see CID.filterIsFirst */
	cidProcessIs?: string[] | string[][]

	cidSessionConfig?: OCidSessionConfig;

	/** Ignorer les steps d'interaction optionnelles **/
	cidPassOptionalInteraction?: boolean;

	/** Appelé à la fin des intéractions CID **/
	onInteractionEndHandler?: (cidSession: CidSession) => Promise<void>;
}


export class CidInteractionByStepsUi extends BaseElementAsync implements ICidUiHandler {
	reg: IReg<IUniverseEnv>;

	conf: OCidInteractionByStepsUiInit;

	cidManifestHead: HTMLElement;
	cidProcessHead: HTMLElement;
	cidDetails: HTMLElement; //select manifest, select process, userWait
	cidInteractions: HTMLIFrameElement;

	cidInfo?: ICidInfos;

	async _initialize(init: OCidInteractionByStepsUiInit) {
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(this.localName, init);
		this.reg = this.findReg(init);

		this.conf = init;

		// Init UI
		this.cidManifestHead = sr.appendChild(<div id="cidManifest"/>);
		this.cidProcessHead = sr.appendChild(<div id="cidProcess"/>);
		this.cidDetails = sr.appendChild(<div id="cidDetails"/>);
		this.cidInteractions = sr.appendChild(<iframe id="cidInteractions"/>) as HTMLIFrameElement;
		if (UiThemeDeskFeat.isIn(desk)) (this.cidInteractions as any).injectTheme = UiThemeDeskFeat.injectTheme;

		// Init de l'interraction CID
		let allHosts: CidHost[];
		switch (init.cidHostUse) {
			case "input":
				allHosts = await CID.getCidHostsForInput(this.reg);
				break;
			case "output":
				allHosts = await CID.getCidHostsForOutput(this.reg);
				break;
			default:
				// both
				allHosts = await CID.getCidHosts(this.reg);
				break;
		}

		this.cidInfo = {
			cidHosts: init.cidHostIs ? Array.from(CID.filterIsOr<CidHost>(allHosts, init.cidHostIs)) : allHosts
		};

		//Fixation des données connues
		if (init.cidManifestUrlInput || this.cidInfo.cidHosts.length !== 1) {
			//Interface de sélection du serveur
			this.cidSeletHost();
		} else {
			// 1 seul serveur connu
			this.cidInfo.lastManifest = await this.cidInfo.cidHosts[0].fetchManifest();
			this.cidOnManifestKnown();
		}
	}

	async overrideMetas(ctx: ICidCtx, metasIn: Dict<any>, foreignMetas: { is: string[], value: string | ((session: CidSession) => string) }[]): Promise<void> {
	}


	/**
	 * Gestion du CID host
	 */
	protected cidSeletHost() {
		DOM.setHidden(this.cidProcessHead, true);
		DOM.setHidden(this.cidInteractions, true);
		this.cidDetails.textContent = null;

		if (this.cidInfo.cidHosts.length > 0) {
			this.cidManifestHead.textContent = "Sélection du serveur cible";
			const group = this.cidDetails.appendChild(<ul/>);
			for (let i = 0, s = this.cidInfo.cidHosts.length; i < s; i++) {
				const host = this.cidInfo.cidHosts[i];
				group.appendChild(<li><a onclick={this.onCidSelectHost} href={i}>{host.label}</a></li>);
			}
			if (this.conf.cidManifestUrlInput) this.buildInputManifest(group.appendChild(<li/>), "Autre");
		} else {
			this.cidManifestHead.textContent = "Définition du serveur cible";
			this.buildInputManifest(this.cidDetails.appendChild(<div id="manifestAlone"/>), "URL du serveur");
		}
	}

	protected async onCidSelectHost(this: HTMLAnchorElement, ev: MouseEvent) {
		ev.preventDefault();
		const me = DOMSH.findHost<CidInteractionByStepsUi>(this);
		me.cidInfo.lastManifest = await me.cidInfo.cidHosts[parseInt(this.getAttribute('href'))].fetchManifest();
		me.cidOnManifestKnown();
	}

	/** ctx.cidInfo.lastManifest renseigné */
	protected async cidOnManifestKnown() {
		const manifest = this.cidInfo.lastManifest;
		if (!manifest) return this.cidSeletHost();
		this.cidManifestHead.textContent = "Serveur : " + CID.getCidManifestLabel(this.cidInfo.cidHosts, manifest);
		if (this.conf.cidManifestUrlInput || this.cidInfo.cidHosts.length !== 1) {
			//Possibilité de changer
			this.cidManifestHead.appendChild(<c-button class="changeBtn" icon="/@skin@/core/actions/cid/change.svg" title="Changer de serveur"
																	 onclick={function (this: Button, ev: MouseEvent) {
																		 return DOMSH.findHost<CidInteractionByStepsUi>(this).cidSeletHost()
																	 }}/>);
		}

		DOM.setHidden(this.cidProcessHead, true);
		//Eval des process
		const prcs = this.cidInfo.cidProcesses = this.conf.cidProcessSelector ? await this.conf.cidProcessSelector({
				reg: this.reg,
				uiContext: this
			}) :
			Array.from(CID.filterIsOr(manifest.processes, this.conf.cidProcessIs || [CidInteractionByStepsUi.PRC_IS_DEFAULT]));
		if (prcs.length !== 1) {
			this.cidSelectProcess();
		} else {
			//1 seul process
			this.cidOnProcessKnown(prcs[0]);
		}
	}

	/**
	 * Host - gestion du manifest libre
	 */
	buildInputManifest(parent: HTMLElement, label: string) {
		parent.appendChild(<div>{label}</div>);
		parent.appendChild(<div id="manifestEdit">
			<input id="manifestInput" type="url" oninput={this.onManifestInput} onchange={function (this: HTMLInputElement) {
				DOMSH.findHost<CidInteractionByStepsUi>(this).onManifestChange(this)
			}}/>
			<c-button id="manifestValid" î={{reg: this.reg, label: "Valider", disabled: true}} onclick={function (this: HTMLInputElement & ICidManifestPointer) {
				DOMSH.findHost<CidInteractionByStepsUi>(this).cidUseManifest(this.cidManifest)
			}}/>
		</div>);
		parent.appendChild(<c-msg id="manifestInputResult" î={{noShadow: true}}/>);
	}

	async onManifestChange(input: HTMLInputElement) {
		const msg = this.shadowRoot.getElementById("manifestInputResult") as MsgLabel;
		const btn = this.shadowRoot.getElementById("manifestValid") as Button & ICidManifestPointer;
		btn.cidManifest = null;
		if (!input.validity.valid) {
			msg.setCustomMsg("Saisissez une URL valide permettant d'obtenir les caractéristiques du serveur (manifeste CID)", "info");
			btn.cidManifest = null;
			btn.disabled = true;
		} else {
			try {
				btn.cidManifest = await CID.getCidManifest(input.value);
			} catch (e) {
			}
			if (btn.cidManifest) {
				msg.setCustomMsg("Serveur reconnu : " + CID.getCidManifestLabel(this.cidInfo.cidHosts, btn.cidManifest), "info");
				btn.disabled = false;
			} else {
				msg.setCustomMsg("Aucun serveur reconnu à cette URL (manifeste CID non trouvé), ou politique de sécurité du serveur incompatible", "error");
				btn.disabled = true;
			}
		}
	}

	protected _cidMnInputWaiter: number;

	onManifestInput(this: HTMLInputElement, ev: Event) {
		const me = DOMSH.findHost<CidInteractionByStepsUi>(this);
		if (me._cidMnInputWaiter) clearTimeout(me._cidMnInputWaiter);
		me._cidMnInputWaiter = setTimeout(() => me.onManifestChange(this), 800) as any;
	}

	cidUseManifest(manifest: CidManifest) {
		if (!manifest) return;
		this.cidInfo.lastManifest = manifest;
		this.cidOnManifestKnown();
	}


	/**
	 * Gestion des process
	 */
	protected cidSelectProcess() {
		DOM.setHidden(this.cidInteractions, true);
		DOM.setHidden(this.cidProcessHead, true);
		//this.cidProcessHead.textContent = "Sélection du processus d'envoi";
		this.cidDetails.textContent = null;
		const group = this.cidDetails.appendChild(<ul/>);
		for (let i = 0, s = this.cidInfo.cidProcesses.length; i < s; i++) {
			const prc = this.cidInfo.cidProcesses[i];
			group.appendChild(<li><a onclick={this.onCidSelectProcess} href={i}>{prc.label.getStr("fr")}</a></li>);
		}
	}

	protected onCidSelectProcess(this: HTMLAnchorElement, ev: MouseEvent) {
		ev.preventDefault();
		const me = DOMSH.findHost<CidInteractionByStepsUi>(this);
		me.cidOnProcessKnown(me.cidInfo.cidProcesses[parseInt(this.getAttribute('href'))]);
	}


	/**
	 * Intéractions CID
	 */
	protected async cidOnProcessKnown(process: CidProcess) {
		DOM.setHidden(this.cidManifestHead, true);
		DOM.setHidden(this.cidProcessHead, true);
		DOM.setHidden(this.cidInteractions, false);
		/* header redondant avec les éléments UI HTML des intéractions
		this.cidProcessHead.textContent = "Processus : " + this.cidInfo.lastProcess.label.getLabel("fr");
		if (this.cidInfo.cidProcesses.length > 1) {
			this.cidProcessHead.appendChild(<c-button class="changeBtn" icon="/@skin@/wsp/views/generators/change.svg" title="Changer de processus"
																								onclick={function (this: Button, ev: MouseEvent) {return DOMSH.findHost<CidInteractionByStepsUi>(this).cidSelectProcess()}}/>);
		}
		*/

		await this.cidRefreshOnSessionChange();

		const cidSession = this.cidInfo.cidSession = await this.createCidSession(process);
		cidSession.onStateChange.add(() => this.cidRefreshOnSessionChange());
		await cidSession.doSession();
		if (cidSession.state === ECidSessionState.failed) {
			//FIXME affichage erreur CID....
			ERROR.reportError({
				msg: "Échec au déploiement..."
			});
		}

	}

	protected async createCidSession(process: CidProcess): Promise<CidSession> {
		const cidSessionConfig = Object.assign({
				manifest: this.cidInfo.lastManifest,
				metasIn: {},
				process: process,
				uiHandler: this,
			},
			this.conf.cidSessionConfig) as OCidSessionConfig;
		await this.overrideMetas({
			reg: this.reg,
			uiContext: this
		}, cidSessionConfig.metasIn, cidSessionConfig.foreignMetas);

		return new CidSession(cidSessionConfig);
	}

	async cidRefreshOnSessionChange() {
		if (this.cidInfo.cidSession) {
			//console.log("cidRefreshOnSessionChange::::", ctx.cidInfo.cidSession);
			switch (this.cidInfo.cidSession.state) {
				case ECidSessionState.authenticating:
					this.cidDetails.textContent = null;
					this.cidDetails.appendChild(<div class="cidUploading"><span>Authentification en cours...</span></div>);
					return;
				case ECidSessionState.inStep:
				case ECidSessionState.uiProcessSel: //en principe impossible
				case ECidSessionState.notStarted: //en principe impossible
					this.cidDetails.textContent = null;
					this.cidDetails.appendChild(<div class="cidUploading"><span>Action en cours...</span></div>);
					return;
				case ECidSessionState.uiAuth:
					// uiWait en cours
					return;
				case ECidSessionState.failed:
				case ECidSessionState.ended:
				case ECidSessionState.aborted:
					this.cidDetails.textContent = null;
					this.cidDetails.appendChild(<div class="cidEnd"><span>Fin de session CID</span></div>);
					// FIXME : afficher ici l'état de la diffusion CID ?
					if (this.conf.onInteractionEndHandler)
						await this.conf.onInteractionEndHandler(this.cidInfo.cidSession);

				//on réaffiche l'état de la dernière diffusion réussie.
			}
		}
		this.cidDetails.textContent = null;
	}

	static PRC_IS_DEFAULT = ["scenari.eu:cid:selectResourceAction", "http://schema.org/FindAction", "http://schema.org/DiscoverAction"];


	//*** ICidUiHandler ***

	async webAuth(session: CidSession, step: CidWebAuthStep): Promise<ICidStepResult> {
		const frame = <iframe style="flex:1"/> as HTMLIFrameElement;
		const dialog = POPUP.showDialog<'ok' | null>(frame, this, {initHeight: "60vh", initWidth: "60vh", titleBar: {barLabel: {label: "Authentification"}, closeButton: {}},});
		dialog.onNextClose().then((r) => {
			if (!r) session.onCidFrameMessage({cidAuth: "failed"})
		});
		const res = await step.initFrame(frame, session);
		dialog.close('ok');
		return res;
	}

	async uiWait(session: CidSession, step: CidStep): Promise<ICidStepResult | null> {
		this.cidDetails.textContent = null;
		return await step.initFrame(this.cidInteractions, session);
	}

	async interact(session: CidSession, step: CidStep): Promise<ICidStepResult> {
		this.cidDetails.textContent = null;
		return await step.initFrame(this.cidInteractions, session);
	}

	async selectProcess(session: CidSession, choices: Set<CidProcess>): Promise<CidProcess | null> {
		console.trace("TODO selectProcess UI");
		return choices.values().next().value;
	}

}

/** Contexte pour les propriétés CID . */
export interface ICidCtx {
	reg: IReg<IUniverseEnv>
	uiContext: HTMLElement
}

export interface ICidInfos {
	/** Liste des servers CID connus compatibles respectant les contraintes définies dans IDatasItemCidNode (cidHostIs...). */
	cidHosts?: CidHost[]
	/** Manifest sélectionné. */
	lastManifest?: CidManifest
	/** Liste des process CID de ce serveur compatibles respectant les contraintes définies dans IDatasItemCidNode (cidProcessIs...). */
	cidProcesses?: CidProcess[]
	/**
	 * CidEnPoints pour cet item.
	 * undefined : pas encore chargé
	 * Promise<JCidEnPoints | null> : chargement en cours
	 * null : aucune donnée dispo pour cet item.
	 * JCidProps : CidProps déjà enregistrées. Plusieurs entrées peuvent être enregistrées en cas de multiples
	 * servers CID ou cidProcess exploités pour ce même IItemPubNode
	 */
	lastCidProps?: JCidStoredProps | null | Promise<JCidStoredProps | null>
	/**
	 * CidEnPoints pour cet item.
	 * undefined : pas encore chargé
	 * Promise<JCidEnPoints | null> : chargement en cours
	 * null : aucune donné dispo pour cet item.
	 * JCidEnPoints : EndPoints existant pour cet item.
	 * @deprecated
	 */
	lastCidEndPoints?: JCidEnPoints | null | Promise<JCidEnPoints | null>
	/** Session prête à être démarrée, en cours ou achevée. */
	cidSession?: CidSession
}

export interface JCidStoredProps {
	store: [{
		manifestUrl: string
		cidProcessIs?: string[] //pas encore utilisé, pourra servir à resélectionner le bon process
		cidProps: Dict<Jsonisable>
	}]
}

/** @deprecated */
export interface JCidEnPoints {
	mvcc: number
	endPoints: [
		{
			manifestUrl: string,
			cidProps: any
		}
	]
}

REG.reg.registerSkin('cid-process-steps', 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
		flex-direction: column;
		min-height: 0;
		min-width: 0;
	}

	[hidden] {
		display: none !important;
	}

	input,
	select,
	textarea {
		background-color: var(--form-bgcolor);
		color: var(--form-color);
		border: 1px solid var(--border-color);
	}

	input:invalid,
	select:invalid,
	textarea:invalid {
		outline: solid 1px #b86262;
	}

	#cidRoot,
	#cidDetails {
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
	}

	#cidInteractions {
		flex: 1;
		border: none;
	}

	#cidRoot {
		overflow: auto;
	}

	#cidManifest {
		margin: .2em;
		font-weight: bold;
		font-size: 1.2em;
		text-align: center;
	}

	#cidProcess {
		margin: .2em;
		font-weight: bold;
		text-align: center;
	}

	#manifestInput {
		flex: 1;
	}

	#manifestAlone {
		margin: 2em;
	}

	#manifestEdit {
		display: flex;
	}

	#manifestInput:focus {
		outline: var(--focus-outline);
	}

	#manifestInputResult {
		font-style: italic;
		color: var(--fade-color);
	}

	.changeBtn {
		display: inline-flex;
	}

	.cidWaiting {
		flex: 1;
		border: none;
		border-top: 1px solid var(--border-color);
	}

	.cidUploading {
		flex: 1;
		display: flex;
		align-items: center;
		font-style: italic;
		margin: 0 2em;
	}

	.cidEnd {
		flex: 1;
		display: flex;
		align-items: center;
		font-style: italic;
		margin: 0 2em;
	}

`);

window.customElements.define("cid-process-steps", CidInteractionByStepsUi);

